import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CapacitySetting } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { can } from '../lib/perms';
import { CZK } from '../lib/format';
import { UzavirkyCard } from '../components/UzavirkyCard';
import { SluzbyCard } from '../components/SluzbyCard';

/**
 * Nastavení — the owner's screen: Kapacity, Uzavírky, Služby — the three
 * things the legacy admin tab let the owner change. Kapacity: the numbers
 * the occupancy panel measures each site against, one per card the panel
 * draws. What the file ships with is shown beside each field; a saved
 * value wins on every dashboard within a minute (the board refetches).
 * Read by everyone who sees the dashboard, written by the owner
 * (/settings/* — the admin's grant).
 */
export function Nastaveni() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const q = useQuery({ queryKey: ['settings', 'capacity'], queryFn: () => api.capacitySettings() });
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!q.data) return;
    const d: Record<string, string> = {};
    for (const r of q.data) d[r.key] = String(r.value ?? r.shipped);
    setDraft(d);
  }, [q.data]);
  const canWrite = can(me.data, '/settings/*', 'write');

  const m = useMutation({
    mutationFn: () => {
      const values: Record<string, number> = {};
      for (const r of q.data ?? []) {
        const v = Number(draft[r.key]);
        if (Number.isInteger(v) && v !== (r.value ?? r.shipped)) values[r.key] = v;
      }
      return api.saveCapacitySettings({ values });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['settings'] });
      await qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
  const err = m.error;
  const message = err instanceof ApiError ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message) : err instanceof Error ? err.message : null;
  const dirty = (q.data ?? []).some((r) => Number(draft[r.key]) !== (r.value ?? r.shipped));
  const valid = (q.data ?? []).every((r) => Number.isInteger(Number(draft[r.key])) && Number(draft[r.key]) >= 0 && Number(draft[r.key]) <= 5000);

  return (
    <div className="container">
      <div className="pagehead">
        <h1>Nastavení</h1>
        <span className="sub">ceníky · dynamické ceny · kapacity · uzavírky · služby</span>
      </div>
      <form
        className="card detailcard"
        onSubmit={(e) => {
          e.preventDefault();
          if (canWrite && dirty && valid && !m.isPending) m.mutate();
        }}
      >
        <h2>Kapacity</h2>
        <p className="settings-note">
          Počet míst, proti kterému panel obsazenosti na Dashboardu měří každé parkoviště. Uložené číslo platí pro všechny;
          vedle je uvedeno, s čím systém přišel.
        </p>
        {q.isPending ? (
          <div className="empty">Načítám…</div>
        ) : q.isError ? (
          <div className="empty" role="alert">Kapacity se nepodařilo načíst.</div>
        ) : (
          <table className="mini settings">
            <thead>
              <tr>
                <th>Parkoviště</th>
                <th className="num">Míst</th>
                <th className="num">Výchozí</th>
                <th>Naposledy změnil</th>
              </tr>
            </thead>
            <tbody>
              {(q.data as CapacitySetting[]).map((r) => (
                <tr key={r.key}>
                  <td>{r.label}</td>
                  <td className="num">
                    <input
                      type="number"
                      min={0}
                      max={5000}
                      step={1}
                      value={draft[r.key] ?? ''}
                      onChange={(e) => setDraft({ ...draft, [r.key]: e.target.value })}
                      disabled={!canWrite}
                      aria-label={`${r.label} — míst`}
                    />
                  </td>
                  <td className="num was">{CZK.format(r.shipped)}</td>
                  <td className="was">{r.updated ? `${r.updatedBy} · ${r.updated.slice(0, 16)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="efactions">
          {canWrite ? (
            <button type="submit" className="btn primary" disabled={!dirty || !valid || m.isPending}>
              {m.isPending ? 'Ukládám…' : 'Uložit kapacity'}
            </button>
          ) : (
            <span className="empty">Kapacity mění majitel.</span>
          )}
          {m.isSuccess && !dirty && <span className="arrived-mark">✔ Uloženo</span>}
          {message && (
            <span className="arrive-error" role="alert">
              {message}
            </span>
          )}
        </div>
      </form>
      <div className="card detailcard">
        <h2>Ceníky</h2>
        <p className="settings-note">
          Ceny podle délky pobytu pro Venku / Garáž / Europort VIP, promo kódy, dynamika a platnost — přesně ty ceníky, ze kterých účtuje web i
          přepážka.
        </p>
        <div className="efactions">
          <Link to="/nastaveni/ceniky" className="btn primary">
            Otevřít ceníky
          </Link>
        </div>
      </div>
      <div className="card detailcard">
        <h2>Dynamické ceny</h2>
        <p className="settings-note">
          Nový cenový systém pro přepážku: sezóny s vlastními tabulkami, příčky podle obsazenosti a předstihu objednávky, slevové kódy a partnerské slevy —
          a přepínač, který ho zapne.
        </p>
        <div className="efactions">
          <Link to="/nastaveni/ceny" className="btn primary">
            Otevřít dynamické ceny
          </Link>
        </div>
      </div>
      {can(me.data, '/users/*', 'read') && (
        <div className="card detailcard">
          <h2>Uživatelé</h2>
          <p className="settings-note">
            Účty zaměstnanců a partnerů: role, ceník, provize a promo kódy partnera, nové heslo. Seznam vidí manažer, mění administrátor.
          </p>
          <div className="efactions">
            <Link to="/uzivatele" className="btn primary">
              Spravovat uživatele
            </Link>
            {canWrite && (
              <Link to="/uzivatele/novy" className="btn">
                + Přidat partnera / uživatele
              </Link>
            )}
          </div>
        </div>
      )}
      <UzavirkyCard canWrite={canWrite} />
      <SluzbyCard canWrite={canWrite} />
    </div>
  );
}
